/**
 * Declarative schema driving the App Settings admin page.
 *
 * Each ``SettingsGroup`` becomes one tab on the page. Each ``SettingField``
 * declares a key (path into AppSettings) + how to render it. Adding a new
 * setting:
 *   1. Add the field to the contract in ``contracts/src/contracts/appSettings.ts``
 *   2. Add a default + (optional) validator to ``app_settings_service.py``
 *   3. Append a row here — the page picks it up automatically
 *
 * Field metadata lives on the frontend only (UI hint material). The
 * canonical validation lives in the backend ``_normalise``; the frontend
 * mirrors a subset for an immediate UX response.
 */

import {
  CHECK_IN_EMAIL_SUBJECT_DEFAULT,
  CHECK_IN_OTP_CAP_BOUNDS,
  CHECK_IN_OTP_CAP_DEFAULTS,
  CHECK_IN_OTP_MESSAGE_DEFAULTS,
  CHECK_IN_OTP_SMS_MAX_LENGTH,
} from '@techgitter/contracts';

export type FieldType =
  | 'integer'
  | 'boolean'
  | 'string'
  | 'timezone'
  | 'businessHours'
  | 'productCodes'
  | 'emailSending';

/** Pure-atomic fields that the generic renderer knows how to draw. */
export interface AtomicField {
  /** Dot-path into AppSettings (e.g. ``"returnGraceDays"``). */
  key: string;
  label: string;
  helperText?: string;
  type: Exclude<FieldType, 'businessHours' | 'productCodes' | 'emailSending'>;
  /** Allowed when the value is meaningfully optional (e.g. null = no
   *  grace period). Atomic types that don't tolerate null leave this off. */
  nullable?: boolean;
  /** Inclusive bounds for integer fields. */
  min?: number;
  max?: number;
  /** Placeholder text for empty inputs. */
  placeholder?: string;
  /** Draw a textarea instead of a single line. For message bodies, where the
   *  admin needs to see the whole thing they are sending. */
  multiline?: boolean;
  /** Rows for a multiline field. Ignored otherwise. */
  rows?: number;
  /** Value the Reset-to-default button restores to (null = "unset"). */
  resetValue?: unknown;
}

/** Composite editor for the BusinessHoursConfig object. */
export interface BusinessHoursField {
  key: 'businessHours';
  label: string;
  helperText?: string;
  type: 'businessHours';
  /** Disabled => save null (= "use code default"). */
  nullable: true;
  /** Reset clears the whole object back to null. */
  resetValue: null;
}

/** Composite editor for the ProductCodeOption[] catalog. */
export interface ProductCodesField {
  key: 'productCodes';
  label: string;
  helperText?: string;
  type: 'productCodes';
  /** Always nullable — null = "use code defaults from contracts". */
  nullable: true;
  /** Reset clears the catalog back to null. */
  resetValue: null;
}

/** Composite editor for the EmailSendingConfig sender registry. */
export interface EmailSendingField {
  key: 'emailSending';
  label: string;
  helperText?: string;
  type: 'emailSending';
  /** Always nullable — null = "use the emailer's built-in registry". */
  nullable: true;
  /** Reset drops back to the built-in registry. */
  resetValue: null;
}

export type SettingField =
  | AtomicField
  | BusinessHoursField
  | ProductCodesField
  | EmailSendingField;

export interface SettingsGroup {
  id: string;
  label: string;
  fields: SettingField[];
}

export const APP_SETTINGS_SCHEMA: SettingsGroup[] = [
  {
    id: 'business-hours',
    label: 'Business Hours',
    fields: [
      {
        key: 'businessHours',
        label: 'Standard business hours',
        type: 'businessHours',
        nullable: true,
        resetValue: null,
        helperText:
          'Drives the time-window classifier (business / shoulder / after-hours), ' +
          'pricing rules, scheduling defaults, and the {{businessHoursStart}} / ' +
          '{{businessHoursEnd}} placeholders in quote T&C templates. Disable to ' +
          'fall back to the application default (Sydney 9–5, Mon–Fri).',
      },
    ],
  },
  {
    id: 'check-in',
    label: 'Check-in',
    fields: [
      {
        key: 'returnGraceDays',
        label: 'Return grace period (days)',
        type: 'integer',
        nullable: true,
        min: 0,
        max: 365,
        placeholder: 'Blank = no grace period',
        resetValue: null,
        helperText:
          "Days after a device's original ticket was resolved during which a " +
          'return check-in is exempt from the diagnostics fee. Beyond this ' +
          'window the diagnostic line shows normally; the tech can still ' +
          'waive it manually via the Waive button.',
      },
      {
        key: 'checkInEmail.subject',
        label: 'Confirmation email subject',
        type: 'string',
        nullable: true,
        placeholder: CHECK_IN_EMAIL_SUBJECT_DEFAULT,
        resetValue: null,
        helperText:
          'Subject of the confirmation email a customer receives once their ' +
          'job has been created and they have accepted the check-in terms. ' +
          'Use {jobNumber} (the RepairShopr ticket number they quote — ' +
          'several devices are joined with commas), {sessionNumber} (the ' +
          'staff-side check-in reference) and {customerName} as placeholders ' +
          '— anything else is left as written. Blank falls back to the ' +
          'default shown above.',
      },
      {
        key: 'checkInMobileVerification',
        label: 'Require mobile verification at check-in',
        type: 'boolean',
        resetValue: null,
        helperText:
          'When on, a customer checking in by QR code must confirm their mobile ' +
          'number with a texted code before they can finish. Confirming binds the ' +
          'terms acceptance to someone with access to that number. Turning this ' +
          'off also releases anyone currently waiting on a code, so it is the ' +
          'switch to reach for if texts stop being delivered — check-ins keep ' +
          'flowing without it. The reception tablet never asks for a code.',
      },
      {
        key: 'checkInOtpMaxSendsPerSession',
        label: 'Max codes per check-in',
        type: 'integer',
        nullable: true,
        min: CHECK_IN_OTP_CAP_BOUNDS.perSession.min,
        max: CHECK_IN_OTP_CAP_BOUNDS.perSession.max,
        placeholder: `Blank = ${CHECK_IN_OTP_CAP_DEFAULTS.perSession}`,
        resetValue: null,
        helperText:
          'How many codes one check-in may request in total. There is already a ' +
          '30-second wait between codes, but that only slows resending — this is ' +
          'what puts a ceiling on it.',
      },
      {
        key: 'checkInOtpMaxSendsPerNumberHour',
        label: 'Max codes to one number per hour',
        type: 'integer',
        nullable: true,
        min: CHECK_IN_OTP_CAP_BOUNDS.perNumberHour.min,
        max: CHECK_IN_OTP_CAP_BOUNDS.perNumberHour.max,
        placeholder: `Blank = ${CHECK_IN_OTP_CAP_DEFAULTS.perNumberHour}`,
        resetValue: null,
        helperText:
          'Counted across every check-in, so it also applies to someone entering ' +
          'a number that is not their own. Raising it increases what a single ' +
          'phone can be sent in an hour.',
      },
      {
        key: 'checkInOtpMaxSendsPerNumberDay',
        label: 'Max codes to one number per day',
        type: 'integer',
        nullable: true,
        min: CHECK_IN_OTP_CAP_BOUNDS.perNumberDay.min,
        max: CHECK_IN_OTP_CAP_BOUNDS.perNumberDay.max,
        placeholder: `Blank = ${CHECK_IN_OTP_CAP_DEFAULTS.perNumberDay}`,
        resetValue: null,
        helperText:
          'Must be at least the hourly figure above, or the two cannot both hold ' +
          'and the save is rejected.',
      },
      {
        key: 'checkInOtpMessage.smsBody',
        label: 'Code message (SMS)',
        type: 'string',
        nullable: true,
        multiline: true,
        rows: 3,
        placeholder: CHECK_IN_OTP_MESSAGE_DEFAULTS.smsBody,
        resetValue: null,
        helperText:
          'The text a customer receives with their one-time code. ' +
          '{code} is required — without it there is nothing for them to type ' +
          'in. {company} and {minutes} are also available; any other ' +
          'placeholder is rejected, because a typo here costs the check-in ' +
          'rather than just looking untidy. Keep the reassurance that we will ' +
          'never ask them to read the code out, and say what the code is for: ' +
          'a bare code with no context is the shape of every phishing text. ' +
          `Limit ${CHECK_IN_OTP_SMS_MAX_LENGTH} characters once filled in — ` +
          'every 160 is a separate SMS the business pays for. Blank restores ' +
          'the wording above.',
      },
      {
        key: 'checkInOtpMessage.emailSubject',
        label: 'Code message subject (email)',
        type: 'string',
        nullable: true,
        placeholder: CHECK_IN_OTP_MESSAGE_DEFAULTS.emailSubject,
        resetValue: null,
        helperText:
          'Used when a customer has no mobile and the code goes by email. ' +
          '{code} is allowed here but the default leaves it out on purpose — ' +
          'the subject is the line most likely to appear on a lock screen or ' +
          'in a notification preview. Blank restores the default.',
      },
      {
        key: 'checkInOtpMessage.emailBody',
        label: 'Code message (email)',
        type: 'string',
        nullable: true,
        multiline: true,
        rows: 4,
        placeholder: CHECK_IN_OTP_MESSAGE_DEFAULTS.emailBody,
        resetValue: null,
        helperText:
          'Plain text, not HTML — blank lines become paragraphs and anything ' +
          'that looks like markup is shown as written rather than rendered. ' +
          'Same placeholder rules as the SMS, and {code} is required. Blank ' +
          'restores the default.',
      },
    ],
  },
  {
    id: 'quotes',
    label: 'Quotes',
    fields: [
      {
        key: 'quoteValidityDays',
        label: 'Quote validity (days after sent)',
        type: 'integer',
        nullable: true,
        min: 0,
        max: 365,
        placeholder: 'Blank = no auto-stamp',
        resetValue: null,
        helperText:
          'When a quote is marked Sent and no Valid Until date is provided, ' +
          'the backend stamps Valid Until = sent date + this many days. ' +
          'Leave blank to disable the auto-stamp (techs set Valid Until ' +
          'manually).',
      },
    ],
  },
  {
    id: 'product-codes',
    label: 'Product Codes',
    fields: [
      {
        key: 'productCodes',
        label: 'Product code catalog',
        type: 'productCodes',
        nullable: true,
        resetValue: null,
        helperText:
          'The list of product codes techs can pick from when adding a retail option ' +
          'to a quote and when filtering disti listings. Each entry needs a code ' +
          '(stored value, also used as the invoice line name) and a label (what techs ' +
          'see). Set device type (e.g. "laptop") on entries that map to a hardware ' +
          'class — the retail dialog stamps it alongside the product code on the ' +
          'quote option / listing. Group is optional and clusters related items under ' +
          'a subheading in the dropdown. Turn the override off to fall back to ' +
          'the built-in defaults.',
      },
    ],
  },
  {
    id: 'email-senders',
    label: 'Email Senders',
    fields: [
      {
        key: 'emailSending',
        label: 'Email sender registry',
        type: 'emailSending',
        nullable: true,
        resetValue: null,
        helperText:
          'Which address each kind of outbound email comes from. Services send ' +
          'a message key (what the message is) and the comms service resolves ' +
          'it here — so moving an address is a change on this page, not a ' +
          'deploy. Disable to use the built-in registry.',
      },
    ],
  },
];
